import { Component, HostListener, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { ScheduleService } from 'src/app/services/schedule-item.service';
import { DateFormatter } from 'src/app/helpers/date-formatter';
import { CustomDateFormatPipe } from 'src/app/pipes/custom-date-format.pipe';
import {
  DxButtonModule,
  DxDateBoxModule,
  DxFormComponent,
  DxFormModule,
  DxGanttModule,
  DxNumberBoxModule,
  DxPopupModule,
  DxScrollViewModule,
  DxSelectBoxModule,
  DxTagBoxModule,
  DxTextBoxModule,
  DxToastModule,
  DxTooltipModule,
} from 'devextreme-angular';
import { DxoLabelModule } from 'devextreme-angular/ui/nested';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectInfoService } from 'src/app/services/project-info.service';
import { ProjectTaskService } from 'src/app/services/project-task.service';
import { ConfigService } from 'src/app/services/config.service';
import { AppGlobals } from 'src/app/app.globals';
import { FeatureFlags } from 'src/app/interfaces/enums/feature-flag';
import { ContactsPopupComponent } from 'src/app/components/contacts-popup/contacts-popup.component';
import { ContactService } from 'src/app/services/contact.service';
import { DeletePopupComponent } from 'src/app/components/delete-popup/delete-popup.component';
import { ProjectService } from 'src/app/services/project.service';
import { ProjectCostsService } from 'src/app/services/project-costs.service';
import { TemplatesService } from 'src/app/services/templates.service';
import { ContactsDropdownBoxComponent } from 'src/app/components/contacts-dropdown-box/contacts-dropdown-box.component';
import { SvgIconComponent } from 'src/app/components/svg-icon/svg-icon.component';
import { GpdButtonComponent } from 'src/app/components/gpd-button/gpd-button.component';
import { KpiCardComponent } from 'src/app/components/kpi-card/kpi-card.component';
import { UserAvatarComponent } from 'src/app/components/user-avatar/user-avatar.component';
import { HolderSelectComponent } from 'src/app/components/holder-select/holder-select.component';
import { DataAccessService } from 'src/app/services/data-access.service';
import { StatusPillComponent } from 'src/app/components/status-pill/status-pill.component';
import { ContractsPillComponent } from 'src/app/components/contracts-pill/contracts-pill.component';
import { ContractService } from 'src/app/services/contracts/contract.service';
import { TaskStatusConfig } from 'src/app/constants/status-pill-configs';
import { StatusData } from 'src/app/constants/Status';
import { AppConfig } from 'src/app/app.config';
import { GlobalPopupService } from 'src/app/components/generic-popup/presets/global-popup/global-popup.service';
import { ProjectCompletePopupComponent } from 'src/app/components/project-complete-popup/project-complete-popup.component';
import { ProjectStatus } from '../../../../../../interfaces/entities/project';
import { ActivityLogService } from 'src/app/services/activity-log.service';
import { ActivityEntity, ActivitySection } from 'src/app/constants/activity-log';

import { SearchableDropdownDirective } from '../../../../../../directives/searchable-dropdown.directive';
import { ProjectImagePipe } from 'src/app/pipes/project-image.pipe';

import { GpdPillComponent } from 'src/app/components/gpd-pill/gpd-pill.component';
import { TaskTag } from 'src/app/interfaces/entities/task-tag';

@Component({
    selector: 'app-project-info-overview',
    imports: [
        CommonModule,
        DxButtonModule,
        DxToastModule,
        DxFormModule,
        DxTextBoxModule,
        DxoLabelModule,
        DxDateBoxModule,
        DxSelectBoxModule,
        DxTagBoxModule,
        DxGanttModule,
        DxPopupModule,
        DxScrollViewModule,
        DxTooltipModule,
        DxNumberBoxModule,
        SvgIconComponent,
        GpdButtonComponent,
        KpiCardComponent,
        UserAvatarComponent,
        HolderSelectComponent,
        ContactsDropdownBoxComponent,
        ContactsPopupComponent,
        DeletePopupComponent,
        StatusPillComponent,
        ContractsPillComponent,
        GpdPillComponent,
        ProjectCompletePopupComponent,
        SearchableDropdownDirective,
        ProjectImagePipe,
    ],
    templateUrl: './project-info-overview.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./project-info-overview.component.css']
})
export class ProjectInfoOverviewComponent implements OnInit, OnDestroy {
  readonly taskStatusConfig = TaskStatusConfig;
  private $unsubscribe = new Subject<void>();
  @ViewChild('editFormGeneral') editFormGeneral: DxFormComponent;
  @ViewChildren('dateboxDueDate') dateboxes: QueryList<any>;

  // ── Overview data ─────────────────────────────────────────────
  overview: any;
  globalOverview: any;
  isFavorite = false;
  fillColor = 'none';
  favoriteToastVisible = false;
  favoriteMessage = '';
  favoriteToastType = 'info';

  // ── Head data ─────────────────────────────────────────────────
  projectHeaddata: any;
  editModeGeneral = false;
  editModeTeam = false;
  editModeFormData: any;
  /** saveChanges() awaits a cost fetch; disables Save/Cancel so the edit cannot be
   * cancelled or re-submitted while the outstanding-costs check is in flight. */
  saving = false;

  get editMode(): boolean {
    return this.editModeGeneral || this.editModeTeam;
  }
  contactsPopupVisible = false;
  deletePopupVisible = false;
  toastVisible = false;
  projectCompletePopupVisible = false;

  get currentUserName(): string {
    return AppGlobals.LoggedInUserData?.userName ?? '';
  }
  field: any;
  fieldType: any;
  companies: any = [];
  persons: any = [];
  users: any;
  departments: any[] = [];
  remunerationTypes: any;
  areaTypeEditOptions: any[] = [];
  phases: any[] = [];
  width = 0;

  projectId = 0;

  // "Daten" section (Feature #4483 / #4524): admin-selected task results for this project.
  // isPill/displayText/pillContent are resolved once, in buildTaskResultRows(), rather than by
  // template-bound methods: a method in a binding re-runs on every change-detection tick, and
  // one returning a fresh object re-triggers app-contracts-pill's ngOnChanges every time with it.
  overviewTaskResults: {
    taskId: number;
    label: string;
    value: string;
    type: string;
    contactType?: string;
    isPill: boolean;
    displayText: string | null;
    pillContent: any;
    tags: TaskTag[];
  }[] = [];

  // Tag filter above "Aufgabenergebnisse" (#4809). No selection = everything, untagged rows
  // included; a selection keeps rows carrying at least one of the picked tags.
  taskTagOptions: TaskTag[] = [];
  selectedTaskTagIds: number[] = [];
  filteredTaskResults: ProjectInfoOverviewComponent['overviewTaskResults'] = [];

  // "Vertragsfristen" section (Feature #4483 / #4526): deadlines from contracts opted in via
  // the "Projektübersicht" checkbox (#4529). Badge design now shares taskStatusConfig with the
  // "Projektstatus" section below, rather than its own bespoke pastel/borderless palette.
  overviewDeadlines: any[] = [];

  /** The whole "Vertragsfristen" section is hidden for tenants without the Contracts feature -
   *  ContractServiceController is [FeatureGate(FeatureFlag.Contracts)], so its endpoints 404 and
   *  ContractService's catch would pop an error toast behind an empty section nobody can fill. */
  readonly contractsEnabled = AppGlobals.UserCan(FeatureFlags.Contracts);

  placeholderNotYetAssigned = $localize`:@@notYetAssigned:Not yet assigned`;
  placeholderNoTitleSet = $localize`:@@noTitleSet:No title set`;
  placeholderUnclassified = $localize`:@@unclassified:Unclassified`;
  placeholderNoneSelected = $localize`:@@noneSelected:None selected`;
  placeholderAssignContact = $localize`:@@assignContact:Assign a contact`;

  projectNumberRequiredMsg = $localize`:@@projectNumberRequired:Project Number is required`;
  projectNameRequiredMsg = $localize`:@@projectNameRequired:Project Name is required`;
  statusDateRequiredMsg = $localize`:@@statusDateRequired:Status Date is required`;
  reasonRequiredMsg = $localize`:@@reasonRequired:Reason is required`;

  outstandingCostsPopupVisible = false;
  outstandingCostsPopupTitle = $localize`:@@outstandingCostsPopupTitle:Note on outstanding costs`;


  areaTypeName = '';
  remunerationTypeNames = '';
  private usersByEmail = new Map<string, any>();

  projectStatusOptions = [ProjectStatus.ACTIVE, ProjectStatus.INACTIVE, ProjectStatus.CANCELED, ProjectStatus.COMPLETED];
  statusEditorOptions = {
    dataSource: this.projectStatusOptions,
    placeholder: $localize`:@@noStatusSet:No status set`,
    onValueChanged: (e: any) => this.onStatusChanged(e.value),
  };

  get showStatusDate(): boolean {
    const s = this.projectHeaddata?.status;
    return s === ProjectStatus.INACTIVE || s === ProjectStatus.CANCELED || s === ProjectStatus.COMPLETED;
  }
  get showReason(): boolean {
    return this.hidesCosts(this.projectHeaddata?.status);
  }
  get isStatusDateReadOnly(): boolean {
    return this.projectHeaddata?.status === ProjectStatus.COMPLETED;
  }
  statusDateEditorOptions: any = {
    readOnly: false,
    displayFormat: 'dd.MM.yyyy',
    placeholder: this.placeholderNoneSelected,
  };
  reasonEditorOptions: any = { placeholder: this.placeholderNoneSelected };

  private refreshStatusDateEditorOptions(): void {
    this.statusDateEditorOptions = {
      readOnly: this.isStatusDateReadOnly,
      displayFormat: 'dd.MM.yyyy',
      placeholder: this.placeholderNoneSelected,
    };
  }

  private getLastCompletedMilestoneDate(): Date | null {
    const milestones: any[] = this.projectHeaddata?.milestones ?? [];
    let latest: Date | null = null;
    for (const m of milestones) {
      if (!m?.completionDate) continue;
      const d = new Date(m.completionDate);
      if (isNaN(d.getTime())) continue;
      if (latest == null || d > latest) latest = d;
    }
    return latest;
  }

  onStatusChanged(newStatus: string): void {
    if (!this.projectHeaddata) return;
    if (newStatus === ProjectStatus.COMPLETED) {
      this.runCompletionCheck();
    } else if (newStatus === ProjectStatus.INACTIVE || newStatus === ProjectStatus.CANCELED) {
      if (!this.projectHeaddata.statusDate) {
        this.projectHeaddata.statusDate = new Date();
      }
    } else if (newStatus === ProjectStatus.ACTIVE) {
      this.projectHeaddata.statusDate = null;
      this.projectHeaddata.statusReason = null;
    }
    this.refreshStatusDateEditorOptions();
  }

  private runCompletionCheck(): void {
    this.service.getProjectCompletionConditions({ ProjectId: this.projectId }).then((result: any) => {
      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
        this.revertStatusFromCompleted();
        return;
      }
      if (result.hasOutstandingCosts) {
        this.revertStatusFromCompleted();
        this.globalPopupService.showWarningPopup(
          $localize`:@@completionBlockedByCosts:There are still outstanding costs in the project. Please review these and settle the outstanding costs or delete costs that are no longer relevant.`
        );
        return;
      }
      if (result.hasBlockingTasks) {
        this.revertStatusFromCompleted();
        const taskLines = (result.blockingTasks as any[])
          .map(t => `• ${t.title} (${t.status}${t.isGridTask ? ', Site task' : ''})`)
          .join('\n');
        const msg =
          $localize`:@@completionBlockedByTasks:There are still tasks in progress, in review or rejected. Please mark them as done or not necessary to complete the project.` +
          '\n\n' + taskLines;
        this.globalPopupService.showWarningPopup(msg);
        return;
      }
      if (result.openTaskCount > 0) {
        this.globalPopupService.showPopup({
          title: $localize`:@@completeProject:Complete project`,
          content: $localize`:@@completionOpenTasksWarning:There are still open tasks in the project. If you mark the project as completed now, these tasks will automatically be set to "not necessary".`,
          buttons: [
            {
              text: $localize`:@@cancel:Cancel`,
              type: 'secondary',
              onClick: () => { this.globalPopupService.closePopup(); this.revertStatusFromCompleted(); },
            },
            {
              text: $localize`:@@confirm:Confirm`,
              onClick: () => { this.globalPopupService.closePopup(); this.executeCompleteProject(true); },
            },
          ],
        });
        return;
      }
      this.executeCompleteProject(false);
    });
  }

  private revertStatusFromCompleted(): void {
    if (this.projectHeaddata) {
      this.projectHeaddata.status = this.editModeFormData?.status ?? ProjectStatus.ACTIVE;
      this.refreshStatusDateEditorOptions();
    }
  }

  private executeCompleteProject(confirmOpenTasks: boolean): void {
    this.service.completeProject({ ProjectId: this.projectId, ConfirmOpenTasksAsNotRequired: confirmOpenTasks }).then((result: any) => {
      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
        this.revertStatusFromCompleted();
        return;
      }
      this.editModeGeneral = false;
      this.editModeTeam = false;
      this.getHeaddata();
      this.projectCompletePopupVisible = true;
      this.projectService.triggerRefreshMilestones();
      this.projectService.triggerRefreshProjectInfo();
    });
  }

  confirmOutstandingCosts(): void {
    this.outstandingCostsPopupVisible = false;
    this.projectHeaddata.freezeProjectCosts = true;
    this.processSaveChanges();
  }

  cancelOutstandingCosts(): void {
    this.outstandingCostsPopupVisible = false;
  }

  // ── Milestone master-detail ───────────────────────────────────
  expandedPhaseNames = new Set<string>();
  expandedMilestoneNames = new Set<string>();
  milestonePackageMap: Record<
    string,
    {
      id: number;
      name: string;
      statusId: number | null;
      completionPercentage: number;
    }[]
  > = {};
  milestonePhaseGroups: {
    phaseName: string;
    scheduleStatusId: number | null;
    milestones: {
      name: string;
      completionPercentage: number;
      scheduleStatusId: number | null;
      daysOverdue: number | null;
      endDate: Date | null;
    }[];
  }[] = [];
  private readonly dateFormatter = new DateFormatter();

  // ── Milestone data ────────────────────────────────────────────
  milestoneGanttVisible = false;
  milestoneLastValidStartDate: any = null;
  milestoneComponent: any = null;
  milestonesRefreshing = false;
  milestonesHaveChanged = false;
  milestonesBackup: any = null;
  milestonesReasonPanelVisible = false;
  milestoneData: any[] = [];

  milestonesChangeReasons = [
    { id: 'Lieferverzug der Komponenten', text: 'Lieferverzug der Komponenten' },
    { id: 'Vertragsverzögerung', text: 'Vertragsverzögerung' },
    { id: 'Vertragsverzögerung (schuldrechtlich)', text: 'Vertragsverzögerung (schuldrechtlich)' },
    { id: 'Verzögerung bei Bewilligung (dinglich)', text: 'Verzögerung bei Bewilligung (dinglich)' },
    { id: 'Verzögerung im Genehmigungsverfahren', text: 'Verzögerung im Genehmigungsverfahren' },
    { id: 'Sonstiges', text: 'Sonstiges' },
  ];

  pageBreadCrumb: any = {
    text: $localize`:@@projectInfoHeader:Project Info Header`,
    href: window.location.href,
    layer: 0,
  };

  constructor(
    private router: Router,
    private service: ProjectInfoService,
    private contractService: ContractService,
    private projectService: ProjectService,
    private configService: ConfigService,
    private contactService: ContactService,
    private templateService: TemplatesService,
    private activatedRoute: ActivatedRoute,
    private appGlobals: AppGlobals,
    private scheduleService: ScheduleService,
    private costsService: ProjectCostsService,
    private globalPopupService: GlobalPopupService,
    private activityLog: ActivityLogService,
    private projectTaskService: ProjectTaskService,
    private dataAccessService: DataAccessService
  ) {}

  // ── Image lightbox ────────────────────────────────────────────
  imageLightboxVisible = false;
  imageZoom = 1;

  get imageZoomPercent(): number {
    return Math.round(this.imageZoom * 100);
  }

  openImageLightbox(): void {
    this.imageZoom = 1;
    this.imageLightboxVisible = true;
  }

  closeImageLightbox(): void {
    this.imageLightboxVisible = false;
  }

  zoomIn(): void {
    this.imageZoom = Math.min(4, +(this.imageZoom + 0.25).toFixed(2));
  }

  zoomOut(): void {
    this.imageZoom = Math.max(0.5, +(this.imageZoom - 0.25).toFixed(2));
  }

  resetZoom(): void {
    this.imageZoom = 1;
  }

  onLightboxOverlayClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('lightbox-overlay')) {
      this.closeImageLightbox();
    }
  }

  onLightboxWheel(event: WheelEvent): void {
    event.preventDefault();
    if (event.deltaY < 0) this.zoomIn();
    else this.zoomOut();
  }

  @HostListener('window:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.imageLightboxVisible) return;
    if (event.key === 'Escape') this.closeImageLightbox();
    if (event.key === '+' || event.key === '=') this.zoomIn();
    if (event.key === '-') this.zoomOut();
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.dateboxes?.first) {
      this.width = this.dateboxes.first.element.nativeElement.offsetWidth;
    }
  }

  ngOnInit(): void {
    this.appGlobals.SetGlobalMenuIndex(2);

    this.activatedRoute.paramMap.pipe(takeUntil(this.$unsubscribe)).subscribe(params => {
      this.projectId = parseInt(params.get('id'));
      this.logProjectView();

      this.service.getProjectOverview({ projectId: this.projectId }).then(result => {
        if (result.hasError) AppGlobals.ShowErrorMessage(result.feedback);
        else this.overview = result.projectOverviewItem;
        return result;
      });

      this.service.getGlobalProjectInfo({ projectId: this.projectId }).then(result => {
        if (result.hasError) AppGlobals.ShowErrorMessage(result.feedback);
        else {
          this.globalOverview = result.globalProjectInfoResultItem;
          this.isFavorite = this.globalOverview.isFavorite;
          this.fillColor = this.isFavorite ? 'var(--color-primary)' : 'none';
        }
      });

      this.service.getProjectOverviewTaskResults({ ProjectId: this.projectId }).then(result => {
        if (result.hasError) AppGlobals.ShowErrorMessage(result.feedback);
        else
          this.buildTaskResultRows(result.items).then(rows => {
            this.overviewTaskResults = rows;
            this.taskTagOptions = uniqueTagsSorted(rows.flatMap(row => row.tags ?? []));
            const known = new Set(this.taskTagOptions.map(t => t.id));
            this.setTaskTagFilter(this.selectedTaskTagIds.filter(id => known.has(id)));
          });
      });

      if (this.contractsEnabled) {
        this.contractService
          .getContractDeadlineTypes({})
          .then(() => this.contractService.getOverviewDeadlinesForProject(this.projectId))
          .then(deadlines => {
            // typeLabel/statusId resolved once here, for the same reason as the task-result rows above.
            this.overviewDeadlines = (deadlines ?? []).map((deadline: any) => ({
              ...deadline,
              typeLabel: this.contractService.getContractDeadlineTypeById(deadline.deadlineTypeId),
              statusId: this.deadlineStatusId(deadline),
            }));
          });
      }

      this.getRemunerationTypes().then(() => this.getHeaddata());
      this.getUsers();
      // #4994: any department can hold a role
      this.dataAccessService.loadDepartments().then(d => (this.departments = d));
    });
  }

  setTaskTagFilter(tagIds: number[]) {
    this.selectedTaskTagIds = tagIds;
    const selected = new Set(tagIds);
    this.filteredTaskResults = selected.size
      ? this.overviewTaskResults.filter(row => (row.tags ?? []).some(tag => selected.has(tag.id)))
      : this.overviewTaskResults;
  }

  toggleTaskTag(tagId: number) {
    this.setTaskTagFilter(
      this.isTaskTagSelected(tagId)
        ? this.selectedTaskTagIds.filter(id => id !== tagId)
        : [...this.selectedTaskTagIds, tagId]
    );
  }

  isTaskTagSelected(tagId: number): boolean {
    return this.selectedTaskTagIds.includes(tagId);
  }

  ngOnDestroy(): void {
    this.$unsubscribe.next();
    this.$unsubscribe.complete();
  }

  private readonly labelTaskResultDone = $localize`:@@done:Done`;
  private readonly customDateFormatPipe = new CustomDateFormatPipe();

  /** A "task" (ProjectTaskTypesEnum.TASK) result is a plain completion boolean - show the
   * localized "Done" label instead of the raw "true"/"false" string. A "date" result is
   * stored as "yyyy-MM-dd" (see dateSerializationFormat on the task's dx-date-box) and is
   * reformatted to the German "dd.MM.yyyy" display format used everywhere else in the app.
   * "contact"/"contract"/"document" results are rendered as pills instead (see
   * taskResultIsPillType / *PillContent below) and never reach this function. Other task
   * types (number, text, ...) already store a human-readable value, shown as-is. */
  taskResultDisplayValue(item: { type: string; value: string }): string {
    if (item.type === 'task') {
      return item.value?.toLowerCase() === 'true' ? this.labelTaskResultDone : null;
    }
    if (item.type === 'date') {
      return this.customDateFormatPipe.transform(item.value) || null;
    }
    return item.value;
  }

  /** "contact"/"contract"/"document" task results are shown as an app-contracts-pill, the
   * same component the Contract Deadlines section uses, instead of raw text/ids. */
  taskResultIsPillType(item: { type: string }): boolean {
    return item.type === 'contact' || item.type === 'contract' || item.type === 'document';
  }

  /** Builds the "Daten" rows with every lookup already resolved, so the template binds plain
   * stable values. Contract designations, document names and contact names each need their own
   * fetch; the rows are published only once those have landed, which also removes the window
   * where a document pill would briefly render its raw numeric id as a name. */
  private async buildTaskResultRows(items: any[]): Promise<any[]> {
    const contractIds = [...new Set(items.filter(i => i.type === 'contract' && i.value).map(i => Number(i.value)))];
    const needsDocs = items.some(i => i.type === 'document' && i.value);
    const [contracts, docResult] = await Promise.all([
      // Deliberately getContractsByIds, not getContracts: the latter republishes whatever it
      // fetched as the app-wide contracts list, so narrowing it to this page's handful of ids
      // would truncate the list every other contracts view reads from.
      // Contract-type task results aren't gated by the "contract" task type itself (that's
      // offered in task config regardless of the Contracts flag), so a flag-off tenant can still
      // have one - same reason the Vertragsfristen section above is skipped when the flag is off.
      this.contractsEnabled ? this.contractService.getContractsByIds(contractIds) : Promise.resolve([]),
      needsDocs ? this.projectTaskService.getProjectTaskDocList({ projectId: this.projectId }) : Promise.resolve(null),
    ]);

    const designationById = new Map<number, string>((contracts ?? []).map((c: any) => [c.id, c.designation]));
    const docsById = new Map<number, any>(
      ((docResult?.hasError ? [] : docResult?.docList) ?? []).map((d: any) => [d.id, d])
    );

    return items.map(item => ({
      ...item,
      isPill: this.taskResultIsPillType(item),
      displayText: this.taskResultDisplayValue(item),
      pillContent: this.buildTaskResultPillContent(item, designationById, docsById),
    }));
  }

  /** Builds the `[content]` object for the pill rendered by a "contact"/"contract"/"document"
   * task result - shape matches whatever app-contracts-pill's ngOnInit expects for that
   * linkType (see contracts-pill.component.ts). Returns null when the referenced record cannot
   * be resolved, so the template falls back to the "no result yet" placeholder. */
  private buildTaskResultPillContent(
    item: { type: string; value: string; contactType?: string; contactName?: string },
    designationById: Map<number, string>,
    docsById: Map<number, any>
  ): any {
    if (!item.value) return null;
    switch (item.type) {
      case 'contract':
        return { contractId: item.value, designation: designationById.get(Number(item.value)) ?? '' };
      case 'document': {
        const doc = docsById.get(Number(item.value));
        // No fallback to item.value - that is the raw document id, and rendering it as a
        // document name reads as real data rather than as a missing lookup.
        return doc ? { documentName: doc.displayName || doc.documentName, externallyModified: !!doc.externallyModified } : null;
      }
      case 'contact':
        // Resolved by GetProjectOverviewTaskResults - see ContactName there.
        return { displayName: item.contactName ?? '' };
    }
    return null;
  }

  /** Maps to StatusData ids so the deadline pill can reuse taskStatusConfig (same badge
   * design as the "Projektstatus" section) instead of its own status scheme. */
  deadlineStatusId(deadline: any): number {
    if (deadline?.done) return StatusData.DONE.id;
    if (deadline?.date && new Date(deadline.date) < this.startOfToday()) return StatusData.OVERDUE.id;
    return StatusData.OPEN.id;
  }

  private startOfToday(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  // paramMap re-emits the current id on init, so guard against logging the same project twice.
  private lastLoggedProjectId: number | null = null;

  private logProjectView(): void {
    if (!this.projectId || this.lastLoggedProjectId === this.projectId) return;
    this.lastLoggedProjectId = this.projectId;
    this.activityLog.logView({
      Section: ActivitySection.PROJECT,
      EntityType: ActivityEntity.PROJECT,
      EntityId: this.projectId,
      ProjectId: this.projectId,
      SiteId: this.siteIdFromRoute,
    });
  }

  private get siteIdFromRoute(): number | undefined {
    for (const route of this.activatedRoute.pathFromRoot) {
      if (route.routeConfig?.path === 'site/:id') {
        return Number(route.snapshot.params.id) || undefined;
      }
    }
    return undefined;
  }

  // ── Computed ──────────────────────────────────────────────────

  get projectImageVersion(): string | null {
    return this.projectHeaddata?.projectImageVersion ?? this.overview?.projectImageVersion ?? null;
  }

  get hasCustomImage(): boolean {
    return !!this.projectHeaddata?.projectImageVersion;
  }

  get statusClass(): string {
    const map: Record<string, string> = {
      Aktiv: 'blue',
      Abgeschlossen: 'green',
      Abgebrochen: 'red',
      Inaktiv: 'orange',
    };
    return map[this.projectHeaddata?.status] ?? 'grey';
  }

  // ── Favorite ──────────────────────────────────────────────────

  toggleProjectFavorite(): void {
    this.isFavorite = !this.isFavorite;
    if (this.isFavorite) this.addProjectAsFavorite();
    else this.removeProjectAsFavorite();
  }

  addProjectAsFavorite(): void {
    this.projectService.addProjectAsFavorite({ ProjectId: this.projectId }).then(result => {
      if (result.hasError) AppGlobals.ShowErrorMessage(result.feedback);
      else {
        this.favoriteMessage = $localize`:@@addedToFavorites:Added to favorites`;
        this.favoriteToastType = 'success';
        this.favoriteToastVisible = true;
        this.fillColor = 'var(--color-primary)';
      }
    });
  }

  removeProjectAsFavorite(): void {
    this.projectService.removeProjectAsFavorite({ ProjectId: this.projectId }).then(result => {
      if (result.hasError) AppGlobals.ShowErrorMessage(result.feedback);
      else {
        this.favoriteMessage = $localize`:@@removedFromFavorites:Removed from favorites`;
        this.favoriteToastType = 'info';
        this.favoriteToastVisible = true;
        this.fillColor = 'none';
      }
    });
  }

  // ── Head data ─────────────────────────────────────────────────

  getHeaddata(): void {
    this.service.getProjectHeaddata({ ProjectHeaddataItem: { projectId: this.projectId } }).then(result => {
      if (result.hasError) AppGlobals.ShowErrorMessage(result.feedback);
      else {
        this.projectHeaddata = result.projectHeaddataItem;
        this.processMilestoneData();
        this.loadMilestoneTaskPackages();
        this.phases = result.projectPhaseDropdownData;
        this.areaTypeEditOptions = result.areaTypeDropdownData;
        this.areaTypeName =
          this.areaTypeEditOptions?.find((a: any) => a.id === this.projectHeaddata?.areaTypeId)?.name ?? '';
        this.remunerationTypeNames =
          this.remunerationTypes?.length && this.projectHeaddata?.remunerationTypes?.length
            ? this.remunerationTypes
                .filter((r: any) => this.projectHeaddata.remunerationTypes.includes(r.id))
                .map((r: any) => r.title)
                .join(', ')
            : '';
      }
    });
  }

  getRemunerationTypes(): Promise<void> {
    return this.templateService.getRemunerationTags({}).then(result => {
      if (!result.hasError) {
        this.remunerationTypes = result.remunerationTags.map((r: any) => ({ id: r.id, title: r.title }));
      }
    });
  }

  // ~880 kB of Companys + Persons, needed only by the contact pickers. Read mode gets its names
  // from the server (projectHeaddata.partnerName/epcName, taskResult.contactName), so this is
  // deferred until an editor that actually offers a choice is opened.
  private contactDataLoaded: Promise<void> | null = null;

  getContactData(): Promise<void> {
    if (this.contactDataLoaded) return this.contactDataLoaded;

    this.contactDataLoaded = Promise.all([
      this.contactService.getCompanies({}).then(result => {
        if (!result.hasError) this.companies = result.companyItems;
      }),
      this.contactService.getPersons({}).then(result => {
        if (!result.hasError) this.persons = result.personItems;
      }),
    ])
      .then(() => undefined)
      .catch(error => {
        // Drop the cached promise so the next picker retries, rather than leaving every contact
        // editor empty for the rest of the session.
        this.contactDataLoaded = null;
        AppGlobals.ShowErrorMessage(error);
      });

    return this.contactDataLoaded;
  }

  getUsers(): void {
    this.configService.getListOfUsers({}).then(result => {
      if (!result.hasError) {
        this.users = result.projectsUsers;
        this.usersByEmail = new Map(this.users.map((u: any) => [u.emailAddress, u]));
      }
    });
  }

  enableEditModeGeneral(): void {
    if (this.editModeTeam) return;
    this.editModeFormData = structuredClone(this.projectHeaddata);
    this.editModeGeneral = true;
    this.refreshStatusDateEditorOptions();
  }

  enableEditModeTeam(): void {
    if (this.editModeGeneral) return;
    this.getContactData();
    this.editModeFormData = structuredClone(this.projectHeaddata);
    this.editModeTeam = true;
  }

  disableEditModeGeneral(): void {
    this.projectHeaddata = structuredClone(this.editModeFormData);
    this.editModeGeneral = false;
  }

  getUserByMail(email: string): any {
    return this.usersByEmail.get(email) ?? null;
  }

  trackByMilestoneId(_index: number, item: any): any {
    return item.milestoneId ?? _index;
  }

  getMilestoneStatusId(ms: { scheduleStatusId: number | null }): number {
    return ms.scheduleStatusId ?? StatusData.OPEN.id;
  }

  getPhaseCompletion(group: {
    completionPercentage: number | null;
    milestones: { completionPercentage: number }[];
  }): number | null {
    if (group.completionPercentage != null) return Math.round(group.completionPercentage);
    if (!group.milestones.length) return null;
    const total = group.milestones.reduce((sum, ms) => sum + (ms.completionPercentage ?? 0), 0);
    return Math.round(total / group.milestones.length);
  }

  getPhaseStatusId(group: { scheduleStatusId: number | null; milestones: { name: string }[] }): number | null {
    if (group.scheduleStatusId === StatusData.OVERDUE.id) return StatusData.OVERDUE.id;
    const withTasks = group.milestones.filter(ms => this.milestoneHasTasks(ms.name));
    if (!withTasks.length) return null;
    return group.scheduleStatusId;
  }
  trackByDepartmentName(_index: number, item: any): any {
    return item.departmentName ?? _index;
  }

  disableEditModeTeam(): void {
    this.restoreMilestoneData();
    this.projectHeaddata = structuredClone(this.editModeFormData);
    this.editModeTeam = false;
  }

  setPopupReturnTarget(field: string, fieldType: string): void {
    this.field = field;
    this.fieldType = fieldType;
  }

  // "partnerId" -> "partnerName". The read-only view binds that field, so it has to follow the
  // selection immediately; the next getHeaddata() re-supplies it from the server.
  private setContactName(idField: string, displayName: string | null): void {
    this.projectHeaddata[idField.replace(/Id$/, 'Name')] = displayName;
  }

  onDropdownContactSelected(e: any, value: string, type: string): void {
    if (e == null) {
      this.projectHeaddata[value] = null;
      this.projectHeaddata[type] = null;
      this.setContactName(value, null);
    } else {
      this.projectHeaddata[value] = e.data?.id ?? null;
      this.projectHeaddata[type] = e.type;
      this.setContactName(value, e.data?.displayName ?? null);
    }
  }

  onPopupContactSelected(e: any, field: any, fieldType: any): void {
    this.projectHeaddata[field] = e.data.id;
    this.projectHeaddata[fieldType] = e.type;
    this.setContactName(field, e.data.displayName ?? null);
    this.contactsPopupVisible = false;
  }

  onNewContactSelected(e: any, field: any, fieldType: any): void {
    this.projectHeaddata[field] = e.id;
    this.projectHeaddata[fieldType] = e.type;
    this.setContactName(field, e.displayName ?? null);
    this.contactsPopupVisible = false;
  }

  showContactsPopup(): void {
    this.getContactData().then(() => (this.contactsPopupVisible = true));
  }

  /** INACTIVE and CANCELED both drop the project out of the cost lists and reports, so both
   * need the outstanding-costs notice; COMPLETED has its own runCompletionCheck() flow. */
  private hidesCosts(status: string): boolean {
    return status === ProjectStatus.INACTIVE || status === ProjectStatus.CANCELED;
  }

  // 114 kB / ~2.5 s for a single boolean, so it is fetched only on a transition that reads it
  // (into INACTIVE/CANCELED) rather than on every page view. Fails open, as the page-load fetch
  // it replaces did: a transient error here must not silently abandon the save.
  private loadOutstandingCosts(): Promise<boolean> {
    return this.costsService
      .getAllProjectCostsForProject({ ProjectId: this.projectId })
      .then((result: any) => !result.hasError && (result.projectCostTotals?.totalOutstandingAmount ?? 0) > 0)
      .catch(() => false);
  }

  async saveChanges(): Promise<void> {
    if (this.editModeGeneral) {
      const result = this.editFormGeneral?.instance.validate();
      if (!result?.isValid) return;
    }
    if (this.projectHeaddata?.status === ProjectStatus.COMPLETED && this.editModeFormData?.status !== ProjectStatus.COMPLETED) {
      // Completion is handled via the runCompletionCheck() popup flow; do not proceed with normal save
      return;
    }
    if (this.hidesCosts(this.projectHeaddata?.status) && !this.hidesCosts(this.editModeFormData?.status)) {
      this.saving = true;
      try {
        if (await this.loadOutstandingCosts()) {
          this.outstandingCostsPopupVisible = true;
          return;
        }
      } finally {
        this.saving = false;
      }
    }
    this.processSaveChanges();
  }

  processSaveChanges(): void {
    this.projectHeaddata.projectPhase = '';
    this.projectHeaddata.nr = this.projectHeaddata.nr != null ? this.projectHeaddata.nr.toString() : null;
    if (this.projectHeaddata.status === ProjectStatus.ACTIVE) {
      this.projectHeaddata.statusDate = null;
      this.projectHeaddata.statusReason = null;
    }

    this.service.writeProjectHeaddata({ ProjectHeaddataItem: this.projectHeaddata }).then(result => {
      if (result.hasError) AppGlobals.ShowErrorMessage(result.feedback);
      else {
        this.editModeGeneral = false;
        this.editModeTeam = false;
        this.getHeaddata();
        this.toastVisible = true;
        this.projectService.triggerRefreshMilestones();
        this.projectService.triggerRefreshProjectInfo();
      }
    });
  }

  uploadProjectImage(file: File): void {
    if (!file) return;
    if (file.name.length > AppConfig.MaxFileNameLength) {
      AppGlobals.ShowErrorMessage(
        $localize`:@@fileNameTooLongError:File name is too long. Maximum allowed length is ${AppConfig.MaxFileNameLength}:maxLength: characters.`
      );
      return;
    }
    const formData = new FormData();
    formData.append('projectId', this.projectId.toString());
    formData.append('projectNr', this.projectHeaddata.nr);
    formData.append('projectName', this.projectHeaddata.pva);
    formData.append('fileBytes', file);

    this.service.uploadProjectImageToBlob(formData).then(result => {
      if (result.hasError) AppGlobals.ShowErrorMessage(result.feedback);
      else this.getHeaddata();
    });
  }

  deleteProjectImage(): void {
    this.service.deleteProjectImage({ projectId: this.projectId }).then(result => {
      if (!result.hasError) this.getHeaddata();
    });
  }

  deleteProject(_e: Event): void {
    this.deletePopupVisible = false;
    this.projectService.deleteProject({ DeleteProjectItem: { ProjectId: Number(this.projectId) } }).then(result => {
      if (result.hasError) AppGlobals.ShowErrorMessage(result.feedback);
      else this.router.navigate([`site/${this.projectHeaddata.gridId}/site-overview`]);
    });
  }

  abortDelete(_e: Event): void {
    this.deletePopupVisible = false;
  }

  // ── Milestone master-detail logic ─────────────────────────────

  milestoneHasTasks(milestoneName: string): boolean {
    return (this.milestonePackageMap[milestoneName] ?? []).some(p => p.statusId !== null);
  }

  togglePhase(name: string): void {
    if (this.expandedPhaseNames.has(name)) this.expandedPhaseNames.delete(name);
    else this.expandedPhaseNames.add(name);
  }

  isPhaseExpanded(name: string): boolean {
    return this.expandedPhaseNames.has(name);
  }

  toggleMilestone(name: string): void {
    if (this.expandedMilestoneNames.has(name)) this.expandedMilestoneNames.delete(name);
    else this.expandedMilestoneNames.add(name);
  }

  isMilestoneExpanded(name: string): boolean {
    return this.expandedMilestoneNames.has(name);
  }

  get allPhasesExpanded(): boolean {
    return (
      this.milestonePhaseGroups.length > 0 &&
      this.milestonePhaseGroups.every(g => this.expandedPhaseNames.has(g.phaseName))
    );
  }

  toggleExpandAll(): void {
    if (this.allPhasesExpanded) {
      this.expandedPhaseNames.clear();
      this.expandedMilestoneNames.clear();
    } else {
      this.milestonePhaseGroups.forEach(g => {
        this.expandedPhaseNames.add(g.phaseName);
        g.milestones.forEach(ms => this.expandedMilestoneNames.add(ms.name));
      });
    }
  }

  navigateToPackage(packageId: number): void {
    this.router.navigate(['../../tasks'], {
      relativeTo: this.activatedRoute,
      queryParams: { packageId },
    });
  }

  getPackageUrl(packageId: number): string {
    const tree = this.router.createUrlTree(['../../tasks'], {
      relativeTo: this.activatedRoute,
      queryParams: { packageId },
    });
    return this.router.serializeUrl(tree);
  }

  onPackageRowClick(event: MouseEvent, pkg: { id: number; statusId: number | null }): void {
    if (pkg.statusId === null) {
      event.preventDefault();
      return;
    }
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    this.navigateToPackage(pkg.id);
  }

  async loadMilestoneTaskPackages(): Promise<void> {
    const scheduleResult = await this.scheduleService.getProjectSchedule(this.projectId);
    if (scheduleResult.hasError || !scheduleResult.schedule?.length) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const items = scheduleResult.schedule.map((s: any) => ({
      id: s.id as string,
      parentId: s.parentId as string | null,
      itemType: s.itemType as string,
      itemId: s.itemId as number,
      title: s.title as string,
      startDate: this.dateFormatter.fromDateOnly(s.startDate),
      endDate: this.dateFormatter.fromDateOnly(s.endDate),
      completionDate: s.completionDate ? this.dateFormatter.fromDateOnly(s.completionDate) : null,
      progress: s.progress as number | null,
      sortingIndex: s.sortingIndex as number,
      isAtRisk: s.isAtRisk as boolean,
    }));

    // ── Build children map for overdue/at-risk propagation ──
    const childrenMap = new Map<string, typeof items>();
    for (const item of items) {
      if (item.parentId) {
        if (!childrenMap.has(item.parentId)) childrenMap.set(item.parentId, []);
        childrenMap.get(item.parentId)!.push(item);
      }
    }
    const overdueAncestors = new Set<string>();
    const markOverdue = (id: string): boolean => {
      for (const child of childrenMap.get(id) ?? []) {
        if ((child.completionDate == null && child.endDate < today) || markOverdue(child.id)) {
          overdueAncestors.add(id);
          return true;
        }
      }
      return false;
    };
    for (const item of items) markOverdue(item.id);

    // Overdue is checked before at-risk (see statusId assignment below), so it always
    // takes precedence - an overdue item never displays as merely at-risk, even if it
    // also satisfies the at-risk pace formula (which it almost always will once overdue).
    const atRiskAncestors = new Set<string>();
    const markAtRisk = (id: string): boolean => {
      for (const child of childrenMap.get(id) ?? []) {
        if ((child.completionDate == null && child.isAtRisk) || markAtRisk(child.id)) {
          atRiskAncestors.add(id);
          return true;
        }
      }
      return false;
    };
    for (const item of items) markAtRisk(item.id);

    // ── Build lookup maps ──
    const statusMap = new Map<string, number>();
    const progressMap = new Map<string, number | null>();
    const endDateMap = new Map<string, Date>();
    for (const item of items) {
      let statusId: number;
      if (item.completionDate != null) statusId = StatusData.DONE.id;
      else if (overdueAncestors.has(item.id) || item.endDate < today) statusId = StatusData.OVERDUE.id;
      else if (atRiskAncestors.has(item.id) || item.isAtRisk) statusId = StatusData.AT_RISK.id;
      else if (item.startDate < today) statusId = StatusData.IN_PROGRESS.id;
      else statusId = StatusData.OPEN.id;
      statusMap.set(item.id, statusId);
      progressMap.set(item.id, item.progress);
      endDateMap.set(item.id, item.endDate);
    }

    // ── Build phase groups directly from schedule hierarchy ──
    const phases = items
      .filter(i => i.itemType === 'Phase')
      .sort((a, b) => a.sortingIndex - b.sortingIndex);

    const map: Record<string, { id: number; name: string; statusId: number | null; completionPercentage: number }[]> = {};

    this.milestonePackageMap = map;
    this.milestonePhaseGroups = phases.map(phase => {
      const milestones = (childrenMap.get(phase.id) ?? [])
        .filter(i => i.itemType === 'Milestone')
        .sort((a, b) => a.sortingIndex - b.sortingIndex)
        .map(ms => {
          const msEndDate = endDateMap.get(ms.id);
          const msStatusId = statusMap.get(ms.id) ?? null;
          const daysOverdue =
            msStatusId === StatusData.OVERDUE.id && msEndDate
              ? Math.max(1, Math.floor((today.getTime() - msEndDate.getTime()) / 86400000))
              : null;

          const packages = (childrenMap.get(ms.id) ?? [])
            .filter(i => i.itemType === 'Package')
            .sort((a, b) => a.sortingIndex - b.sortingIndex)
            .map(pkg => ({
              id: pkg.itemId,
              name: pkg.title,
              statusId: statusMap.get(pkg.id) ?? null,
              completionPercentage: progressMap.get(pkg.id) ?? 0,
            }));

          if (packages.length) map[ms.title] = packages;

          return {
            name: ms.title,
            completionPercentage: progressMap.get(ms.id) ?? 0,
            scheduleStatusId: msStatusId,
            daysOverdue,
            endDate: msEndDate ?? null,
          };
        });

      return {
        phaseName: phase.title,
        scheduleStatusId: statusMap.get(phase.id) ?? null,
        completionPercentage: progressMap.get(phase.id) ?? null,
        milestones,
      };
    }).filter(g => g.milestones.length);

    // Auto-expand stops at the milestone level: expand all phases except fully
    // completed ones (so milestones are visible), but leave milestones collapsed
    // so task packages are only shown on manual expand.
    for (const group of this.milestonePhaseGroups) {
      if (group.scheduleStatusId !== StatusData.DONE.id) {
        this.expandedPhaseNames.add(group.phaseName);
      }
    }
  }

  // ── Milestone logic ───────────────────────────────────────────

  processMilestoneData(): void {
    this.milestoneData = this.projectHeaddata.milestones ?? [];
    if (!this.milestoneData?.length) return;

    if (!this.milestonesBackup) this.backupMilestoneData();
    this.milestonesHaveChanged = false;

    for (let i = 0; i < this.milestoneData.length; i++) {
      const m = this.milestoneData[i];
      m.hasChanged = false;
      m.dpDueDate = m.dueDate;
      m.deletionReason = '';
      m.index = i;
      m.wasBlank = m.dueDate == null;

      if (i === 0) {
        m.startDate = new Date(this.projectHeaddata.projectStartDate);
        m.previousMilestone = null;
        this.milestoneLastValidStartDate = m.dueDate ?? m.startDate;
        continue;
      }

      m.startDate = this.milestoneLastValidStartDate;
      if (m.dueDate != null && m.dueDate < m.startDate) m.dueDate = m.startDate;
      m.previousMilestone = this.milestoneData[i - 1];
      if (m.dueDate != null) this.milestoneLastValidStartDate = m.dueDate;
    }
  }

  backupMilestoneData(): void {
    this.milestonesBackup = JSON.parse(JSON.stringify(this.milestoneData));
  }

  restoreMilestoneData(): void {
    this.processMilestoneData();
  }

  handleSaveMilestonesChangeReasons(): void {
    this.milestonesReasonPanelVisible = false;
    this.processSaveChanges();
  }

  handleMilestoneDataChange(changedId: number, doRefresh: boolean): void {
    if (this.milestonesRefreshing) return;
    const milestoneItem = this.milestoneData.find((m: any) => m.milestoneId === changedId);
    if (!milestoneItem) return;

    if (!milestoneItem.wasBlank) {
      milestoneItem.hasChanged = true;
      this.milestonesHaveChanged = true;
    }

    let newStartDate = milestoneItem.dueDate;
    for (let i = 0; i < this.milestoneData.length; i++) {
      if (i <= milestoneItem.index) continue;
      const m = this.milestoneData[i];
      if (m.dueDate == null) {
        m.startDate = newStartDate;
        continue;
      }
      if (m.startDate?.getTime == undefined) m.startDate = new Date(m.startDate);
      if (m.dueDate?.getTime == undefined) m.dueDate = new Date(m.dueDate);
      const diff = newStartDate - m.startDate.getTime();
      m.startDate = newStartDate;
      m.dueDate = new Date(m.dueDate.getTime() + diff);
      m.dpDueDate = m.dueDate;
      newStartDate = m.dueDate;
    }

    if (doRefresh) {
      this.milestonesRefreshing = true;
      this.milestoneGanttRefresh();
      setTimeout(() => {
        this.milestonesRefreshing = false;
      }, 1000);
    }
  }

  handleMilestoneTaskEditDialogShowing(e: any): void {
    e.cancel = true;
  }

  handleMilestoneDateChange(e: any, data: any): void {
    if (!e?.value) {
      e.cancel = true;
      return;
    }
    const m = this.milestoneData.find((m: any) => m.milestoneId === data.milestoneId);
    if (!m) {
      e.cancel = true;
      return;
    }
    const newDate = new Date(e.value);
    m.dueDate = newDate;
    m.dpDueDate = newDate;
    this.handleMilestoneDataChange(m.milestoneId, true);
  }

  handleMilestoneTaskMoving(e: any): void {
    this.milestoneComponent = e.component;
    e.cancel = true;
  }

  handleMilestoneTaskUpdated(e: any): void {
    this.milestoneComponent = e.component;
    const m = this.milestoneData.find((m: any) => m.milestoneId === e.key);
    if (!m || !e.values.dueDate) return;
    if (e.values.dueDate.getTime == undefined) e.values.dueDate = new Date(e.values.dueDate);
    m.dueDate = e.values.dueDate;
    m.dpDueDate = e.values.dueDate;
    this.handleMilestoneDataChange(e.key, true);
  }

  handleMilestoneTaskUpdating(e: any): void {
    if (this.milestonesRefreshing || e.newValues.startDate) e.cancel = true;
  }

  handleMilestoneTaskClick(e: any): void {
    this.milestoneComponent = e.component;
  }

  handleMilestoneGanttLoad(e: any): void {
    this.milestoneComponent = e.component;
    setTimeout(() => this.milestoneGanttRefresh(), 1000);
  }

  showMilestoneGantt(): void {
    this.milestoneGanttVisible = true;
    setTimeout(() => this.milestoneGanttRefresh(), 1000);
  }

  milestoneGanttRefresh(): void {
    this.milestoneComponent?.refresh();
  }
}

function uniqueTagsSorted(tags: TaskTag[]): TaskTag[] {
  const byId = new Map<number, TaskTag>();
  tags.forEach(tag => byId.set(tag.id, tag));
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}
